import { useState } from 'react';

export const useLocalStorage = <T>(
  key: string,
  defaultValue: T
): [T, (value: T) => void] => {
  const [item, setItem] = useState(
    JSON.parse(localStorage.getItem(key)) || { value: defaultValue }
  );

  const handleSetItem = (value: T) => {
    localStorage.setItem(key, JSON.stringify({ value }));
    setItem({ value });
  };

  return [item.value, handleSetItem];
};

/**
 * Manage local storage of an object with extensible properties.
 *
 * This hook allows you to store an object in local storage. If code changes are made that add
 * new properties to the object, the hook will reflect the passed initial value for the new properties,
 * while preserving the existing properties for old values found in local storage.
 */
export const useExtensibleLocalStorage = <T extends Record<string, unknown>>(
  key: string,
  defaultValue: T
): [T, (value: T) => void] => {
  const valueFromStorage = JSON.parse(localStorage.getItem(key));
  const initialValue = valueFromStorage
    ? { ...defaultValue, ...valueFromStorage.value }
    : defaultValue;

  const [item, setItem] = useState({ value: initialValue });

  const handleSetItem = (value: T) => {
    localStorage.setItem(key, JSON.stringify({ value }));
    setItem({ value });
  };

  return [item.value, handleSetItem];
};
